// COMPONENTS
import Box from '@/main/components/shared/box';
import Button from '@/main/components/shared/button/button';
import { AreaButton } from '@/main/components/shared/styles';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import * as S from './styles';
import { SearchFilterRegionRequest } from '@/main/models/request';
import { useEffect, useState } from 'react';
import {
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey,
  removeEmptyValues
} from '@/main/services/utils';
import { useDispatch } from 'react-redux';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getDistributionRegion,
  useDistributionRegionReducer
} from '@/main/store/distributionRegion';
import { KeyValue } from '@/main/models/common/keyValue';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { isNullUndefined } from '@/main/services/reducerUtils';
import { resetReducerCompany } from '@/main/store/company';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import ModalRegion from '../modalRegion/modalRegion';

interface IFilterRegionScreenWrapper {
  onResetSearch: () => void;
  getDataRegion: (filters: SearchFilterRegionRequest) => void;
  formFilters?: SearchFilterRegionRequest | null;
}

interface IFilterRegionScreen {
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  onResetSearch: () => void;
  getDataRegion: (filters: SearchFilterRegionRequest) => void;
}

function FilterRegionScreen({
  showFilters,
  setShowFilters,
  onResetSearch,
  getDataRegion
}: Readonly<IFilterRegionScreen>) {
  //#region Hooks
  const dispatch = useDispatch();
  //#endregion

  //#region Reducers
  const { countriesUser: countryReducer } = useCountryReducer();
  const { distributionRegion: regionReducer } = useDistributionRegionReducer();
  //#endregion

  //#region states
  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.data
  );

  const optionDefaultRegionDrop = getDefaultOptionDrop(regionReducer.data);

  const INITIAL_FORM_STATE = {
    country: null as KeyValue | null,
    region: null as KeyValue | null,
    startPostalCode: null as string | null,
    endPostalCode: null as string | null
  };

  type ISearchRegionForm = typeof INITIAL_FORM_STATE;
  const methods = useFormQueryParams<ISearchRegionForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      country: countryReducer,
      region: regionReducer
    },
    onSubmit: onSubmit
  });
  const {
    control,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    customHandleSubmit,
    setError
  } = methods;
  //#endregion

  function handleClear() {
    onResetSearch();
    reset(INITIAL_FORM_STATE);
    setValue('country', optionDefaultCountryDrop);
    clearErrors();
  }
  function onSubmit(formInputs: ISearchRegionForm) {
    const start = formInputs.startPostalCode ? Number(formInputs.startPostalCode) : null;
    const end = formInputs.endPostalCode ? Number(formInputs.endPostalCode) : null;

    if (start !== null && end !== null && end < start) {
        setError('endPostalCode', {
            type: 'endPostalCode',
            message: getTranslateByKey(
              'information.initialLessEndZipcode',
              true
            )
          });
          return;
    }

    const request: SearchFilterRegionRequest = {
      guidCountry: String(formInputs.country?.key),
      name: isNullUndefined(formInputs.region?.value) 
        ? null 
        : String(formInputs.region?.value),
      initialZipCode: isNullUndefined(formInputs.startPostalCode)
        ? null
        : Number(formInputs.startPostalCode),
      finalZipCode: isNullUndefined(formInputs.endPostalCode)
        ? null
        : Number(formInputs.endPostalCode)
    };

    getDataRegion(removeEmptyValues(request));
    setShowFilters(false);
  }

  //#region useefctts
  useEffect(() => {
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());

    return () => {
      dispatch(resetReducerCompany());
    };
  }, []);

  useEffect(() => {
    if (!isNullUndefined(optionDefaultCountryDrop)) {
      dispatch(
        getDistributionRegion({
          guidCountry: String(optionDefaultCountryDrop?.key)
        })
      );
    }
  }, [optionDefaultCountryDrop]);
  //#endregion

  //#region Render constants
  const ID_SCREEN = 'id-searchRegion';
  const generateIdScreen = (id: string) => `${ID_SCREEN}-${id}`;
  const hasLoadingReducers = countryReducer.isLoading;
  //#endregion

  return (
    <Box showBox={showFilters} title={getTranslateByKey('information.filters')}>
      <form onSubmit={customHandleSubmit} noValidate>
        <S.RowInputs>
          <KogAutoCompleteControlled
            required
            id={generateIdScreen('select-country')}
            nameCustom="country"
            control={control}
            label={getTranslateByKey('information.country')}
            options={countryReducer.data}
            isLoading={countryReducer.isLoading}
            rulesCustom={{ required: true }}
            errorMessageCustom={
              errors.country?.type === 'required'
                ? getTranslateByKey('information.countryRequired', true)
                : ''
            }
            onChangeCustom={value => {
              dispatch(
                getDistributionRegion({
                  guidCountry: String(value?.key)
                })
              );
            }}
            hasErrorCustom={!!errors.country}
            valueDefaultKey={optionDefaultCountryDrop?.key}
            setValueCustom={setValue}
            disabled={optionDefaultCountryDrop !== null}
          />
          <KogAutoCompleteControlled
            required
            id={generateIdScreen('select-region')}
            nameCustom="region"
            control={control}
            label={getTranslateByKey('information.regionName')}
            options={regionReducer.data}
            isLoading={regionReducer.isLoading}
            onChangeCustom={() => {}}
            hasErrorCustom={!!errors.region}
            valueDefaultKey={optionDefaultRegionDrop?.key}
            setValueCustom={setValue}
            disabled={optionDefaultRegionDrop !== null}
          />
          <KogInputTextControlledNew
            name="startPostalCode"
            label={getTranslateByKey('information.initialZipCode')}
            hasOnlyNumber
            control={control}
            inputProps={{ maxLength: 5 }}
            onChangeCustom={() => {}}
            id="id-register-txt-startPostalCode"
          />

          <KogInputTextControlledNew
            name="endPostalCode"
            label={getTranslateByKey('information.finalZipCode')}
            hasOnlyNumber
            control={control}
            inputProps={{ maxLength: 5 }}
            onChangeCustom={() => {}}
            id="id-register-txt-endPostalCode"
          />
        </S.RowInputs>
        <S.AreaButtons>
            <SecondaryButton
              id={generateIdScreen('btn-submit')}
              disabled={hasLoadingReducers}
              type="submit">
              {getTranslateByKey('information.search')}
            </SecondaryButton>
            <SecondaryButton
              id={generateIdScreen('btn-clear')}
              type="button"
              onClick={handleClear}>
              {getTranslateByKey('information.clear')}
            </SecondaryButton>
          </S.AreaButtons>
      </form>
    </Box>
  );
}

function FilterRegionScreenWrapper(
  props: Readonly<IFilterRegionScreenWrapper>
) {
  //#region States
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [isOpenModalRegion, setIsOpenModalRegion] = useState<boolean>(false);
  //#endregion

  //#region HandleFunctions
  function handleFilters() {
    setShowFilters(prev => !prev);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  //#endregion

  const mergedProps: IFilterRegionScreen = {
    ...props,
    showFilters,
    setShowFilters
  };

  function handleSave(){
    setIsOpenModalRegion(true)
  }

  function refreshSearchGrid() {
    const currentFilters = props.formFilters;
    if(!isNullUndefined(currentFilters)){
      props.getDataRegion(currentFilters)
    }
  }

  return (
    <>
      <AreaButton>
        <Button onClick={handleFilters} id="id-searchRegionScreen-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
        <Button onClick={handleSave} id="id-searchRegionScreen-btn-new-region">
          {getTranslateByKey('information.newRegion')}
        </Button>
      </AreaButton>
      <FilterRegionScreen {...mergedProps} />

      <ModalRegion
        isOpenModal={isOpenModalRegion}
        closeModal={() => setIsOpenModalRegion(false)}
        setIsOpenModal={setIsOpenModalRegion}
        refreshSearchGrid={refreshSearchGrid}
      />
    </>
  );
}

export default FilterRegionScreenWrapper;

